// 🔧 content_item_model.js
// ✅ Purpose: Canonical Content Engine model for one editable content item
// ✅ Features:
// - Creates normalized Content Item objects before they are saved to Firestore
// - Supports global content in Phase 1
// - Includes future-ready fields for localization, AI drafting, accessibility, media, profiles, markets, events, and organizations
// - Keeps Firestore document shape consistent across admin tools, bundle builders, and future migration scripts
// - Prevents pages from defining their own content document structures

import {
  CONTENT_STATUSES,
} from "../constants/content_statuses.js";

import {
  DEFAULT_CONTENT_SCOPE,
  DEFAULT_SCOPE_ID,
} from "../constants/content_scopes.js";

import {
  DEFAULT_CONTENT_LOCALE,
} from "../constants/content_locales.js";

import {
  DEFAULT_CONTENT_PROFILE,
} from "../constants/content_profiles.js";

import {
  parseContentKey,
} from "../helpers/content_key_helpers.js";

/**
 * Creates a canonical Content Item.
 *
 * This model is the editable source of truth.
 */
export function createContentItem({
  key,
  type,
  value = "",
  defaultValue = "",

  area,
  section,
  element,

  scope = DEFAULT_CONTENT_SCOPE,
  scopeId = DEFAULT_SCOPE_ID,

  locale = DEFAULT_CONTENT_LOCALE,
  profile = DEFAULT_CONTENT_PROFILE,
  market = null,

  organizationId = null,
  eventId = null,

  status = CONTENT_STATUSES.DRAFT,
  version = 1,

  purpose = null,
  audience = [],
  ownerEngine = "content_engine",
  consumerEngines = [],

  tags = [],

  trustLevel = "low",
  requiresReview = false,
  isSystemCritical = false,

  isLocalized = true,
  allowOrganizationOverride = true,

  aiEnabled = true,
  aiPromptType = null,
  knowledgeModules = [],
  voiceProfile = "lgv_default",

  media = null,
  accessibility = null,

  createdAt = null,
  createdBy = null,
  updatedAt = null,
  updatedBy = null,
  publishedAt = null,
  publishedBy = null,
} = {}) {
  const parsedKey = parseContentKey(key);

  const resolvedArea = area || parsedKey.area;
  const resolvedSection = section || parsedKey.section;
  const resolvedElement = element || parsedKey.element;

  return {
    key,
    type,

    value,
    defaultValue,

    area: resolvedArea,
    section: resolvedSection,
    element: resolvedElement,

    scope,
    scopeId,

    locale,
    profile,
    market,

    organizationId,
    eventId,

    status,
    version,

    purpose,
    audience,
    ownerEngine,
    consumerEngines,

    tags,

    trustLevel,
    requiresReview,
    isSystemCritical,

    isLocalized,
    allowOrganizationOverride,

    ai: {
      enabled: aiEnabled,
      promptType: aiPromptType,
      knowledgeModules,
      voiceProfile,
      lastGeneratedAt: null,
      lastGeneratedBy: null,
    },

    media,

    accessibility,

    createdAt,
    createdBy,
    updatedAt,
    updatedBy,
    publishedAt,
    publishedBy,
  };
}

/**
 * Returns true when the content item is published.
 */
export function isContentItemPublished(contentItem = {}) {
  return contentItem.status === CONTENT_STATUSES.PUBLISHED;
}

/**
 * Returns true when the content item should be included in a public bundle.
 */
export function shouldIncludeContentItemInBundle(contentItem = {}) {
  return Boolean(
    contentItem.key &&
      contentItem.area &&
      contentItem.section &&
      contentItem.element &&
      contentItem.status === CONTENT_STATUSES.PUBLISHED
  );
}